<script setup lang="ts">
import { computed, watch, watchEffect } from "vue";
import { RouterView } from "vue-router";
import { useAuthStore, useAwardStore } from "@/stores";
import Nav from "@/components/Nav/Nav.vue";
import { GDialogRoot } from "gitart-vue-dialog";
import { useRouter, useRoute } from "vue-router";
import semver from "semver";
import useAwards from "@/composables/use-awards";
import useCandidate from "@/composables/use-candidate";
import { getDefaultAwardTable } from "./stores/award";

const awardStore = useAwardStore();
const authStore = useAuthStore();

const { settings } = useAwards();
useCandidate();

watchEffect(async () => {
  const latestClientVersion = await awardStore.getLatestClientVersion();
  const currentClientVersion = semver.valid(
    import.meta.env.VITE_BOA_CLIENT_VERSION
  );
  console.log({ latestClientVersion, currentClientVersion });
  if (
    latestClientVersion &&
    currentClientVersion &&
    semver.gt(latestClientVersion, currentClientVersion)
  ) {
    if (!authStore.reloading) {
      authStore.reloading = true;
      window.location.reload();
    }
  }
});

const hasUpdate = computed(() => {
  const latestClientVersion = semver.valid(authStore.latestClientVersion);
  if (!latestClientVersion) {
    return false;
  }
  const currentClientVersion = semver.valid(
    import.meta.env.VITE_BOA_CLIENT_VERSION
  );
  return (
    latestClientVersion &&
    currentClientVersion &&
    semver.gt(latestClientVersion, currentClientVersion)
  );
});

const hideNav = computed(() => {
  const route = useRoute();
  return !route?.matched?.length || route.meta?.hideNav;
});

const router = useRouter();
const route = useRoute();

watch(
  () => authStore.authenticated,
  (authenticated, prevAuthenticated) => {
    if (prevAuthenticated && !authenticated) {
      return router.replace("/login");
    }
    if (
      authenticated &&
      authStore.isCandidate &&
      !authStore.user.candidateVerified
    ) {
      if (route.meta?.requiresAuth) {
        return router.replace("/");
      }
    }
  }
);

function reload() {
  window.location.reload();
}
</script>

<template>
  <div class="h-full" :class="getDefaultAwardTable()">
    <LoadingOverlay :is-loading="!$route.matched?.length || !settings" />
    <div class="flex h-full flex-col bg-boa-gray md:flex-row" v-if="settings">
      <Nav v-show="!hideNav" class="shrink-0 grow-0" />
      <main
        class="relative flex h-full w-full grow-0 flex-col overflow-auto p-6"
      >
        <Suspense>
          <RouterView />
          <template #fallback>
            <LoadingOverlay :is-loading="true" />
          </template>
        </Suspense>
      </main>
      <button
        v-if="authStore.isAdmin && hasUpdate"
        class="fixed top-0 inset-x-0 bg-primary text-white font-medium py-1"
        @click="() => reload()"
      >
        Refresh for Dashboard Update
      </button>
      <GDialogRoot />
    </div>
  </div>
</template>
